import { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { usePlatform } from "../../../../contexts/PlatformContext";
import { listPortfolio, assignCsm, transferCsm } from "../../../../services/platform_csm_service";
import { listUsers } from "../../../../services/platform_admin_service";
import { MANAGER_ROLES, PORTFOLIO_VIEWS } from "../constants";

const usePlatformPortfolio = () => {
    const { user } = useContext(AuthContext);
    const { platformRole } = usePlatform();

    const isManager = MANAGER_ROLES.includes(platformRole);

    const [view, setView] = useState(() => {
        return platformRole === "comercial" ? PORTFOLIO_VIEWS.sales : PORTFOLIO_VIEWS.csm;
    });
    const [items, setItems] = useState([]);
    const [teamUsers, setTeamUsers] = useState([]);
    const [filterCsmId, setFilterCsmId] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [transferModal, setTransferModal] = useState({ open: false, client: null, clients: [] });
    const [selectedIds, setSelectedIds] = useState([]);

    const fetchPortfolio = useCallback(async (currentView = view, csmFilter = filterCsmId) => {
        try {
            setLoading(true);
            setError(null);
            const params = { view: currentView };
            // Gestores podem ver carteira filtrada por outro CS
            if (isManager && currentView === PORTFOLIO_VIEWS.all && csmFilter) {
                params.filterByCsmId = csmFilter;
            }
            const result = await listPortfolio(params);
            setItems(result.items || []);
        } catch (err) {
            console.error("[usePlatformPortfolio]", err);
            setError(err.message || "Erro ao carregar carteira.");
        } finally {
            setLoading(false);
        }
    }, [view, filterCsmId, isManager]);

    const fetchTeam = useCallback(async () => {
        if (!isManager) return;
        try {
            const result = await listUsers({ page: 1, pageSize: 100 });
            setTeamUsers(result.items || []);
        } catch (_) { /* silencioso */ }
    }, [isManager]);

    useEffect(() => {
        if (!platformRole) return;
        const defaultView = platformRole === "comercial" ? PORTFOLIO_VIEWS.sales : PORTFOLIO_VIEWS.csm;
        setView(defaultView);
        fetchPortfolio(defaultView, "");
    }, [platformRole, fetchPortfolio]);
    useEffect(() => { fetchTeam(); }, [fetchTeam]);

    const changeView = useCallback((newView) => {
        setView(newView);
        setFilterCsmId("");
        setSearch("");
        setSelectedIds([]);
        fetchPortfolio(newView, "");
    }, [fetchPortfolio]);

    const applyFilterCsm = useCallback((csmId) => {
        setFilterCsmId(csmId);
        fetchPortfolio(PORTFOLIO_VIEWS.all, csmId);
    }, [fetchPortfolio]);

    // Filtragem client-side por nome
    const filteredItems = items.filter(c => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (c.name || "").toLowerCase().includes(q) ||
            (c.csmName || "").toLowerCase().includes(q) ||
            (c.salesConsultantName || "").toLowerCase().includes(q)
        );
    });

    const handleAssignCsm = async ({ idTenant, csmUid, csmName }) => {
        try {
            await assignCsm({ idTenant, csmUid, csmName });
            toast.success(`CS ${csmName} atribuído com sucesso.`);
            fetchPortfolio(view, filterCsmId);
        } catch (err) {
            toast.error(err.message || "Erro ao atribuir CS.");
            throw err;
        }
    };

    const handleTransferCsm = async ({ idTenant, idTenants = [], newCsmUid, newCsmName, reason }) => {
        try {
            const targets = idTenants && idTenants.length > 0 ? idTenants : [idTenant];
            
            // Execute in parallel via Promise.all
            await Promise.all(
                targets.map(id => transferCsm({ idTenant: id, newCsmUid, newCsmName, reason }))
            );
            
            toast.success(`${targets.length} cliente(s) transferido(s) para ${newCsmName}.`);
            setTransferModal({ open: false, client: null, clients: [] });
            setSelectedIds([]); // Clear batch selection
            fetchPortfolio(view, filterCsmId);
        } catch (err) {
            toast.error(err.message || "Erro ao transferir cliente(s).");
            throw err;
        }
    };

    const openTransfer = (client, clients = []) => setTransferModal({ open: true, client, clients });
    const closeTransfer = () => setTransferModal({ open: false, client: null, clients: [] });

    const toggleSelectRow = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (ids, isAllSelected) => {
        if (isAllSelected) {
            setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
        } else {
            setSelectedIds(prev => {
                const newIds = ids.filter(id => !prev.includes(id));
                return [...prev, ...newIds];
            });
        }
    };

    return {
        view,
        items: filteredItems,
        totalItems: items.length,
        teamUsers,
        filterCsmId,
        search,
        loading,
        error,
        isManager,
        currentUserId: user?.uid,
        selectedClient,
        transferModal,
        setSearch,
        changeView,
        applyFilterCsm,
        setSelectedClient,
        handleAssignCsm,
        handleTransferCsm,
        openTransfer,
        closeTransfer,
        selectedIds,
        setSelectedIds,
        toggleSelectRow,
        handleSelectAll,
        platformRole,
        refetch: () => fetchPortfolio(view, filterCsmId),
    };
};

export default usePlatformPortfolio;
